import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Card from '../components/Card.jsx';
import StatCard from '../components/StatCard.jsx';
import Chip from '../components/Chip.jsx';
import { msaApi } from '../lib/apiClient.js';
import { planCardClassName, TIER_PLANS, universeCountLabel } from './plans.js';
import useUniverseSummary from '../hooks/useUniverseSummary.js';
import { number } from '../utils/format.js';
import { indexMeta } from '../utils/marketPresentation.js';

function workspaceModules(universeCount) { return [
  { to: '/app/briefings', title: '① Daily Briefings', desc: 'Editorial market note, index strip, top movers, corporate-action calendar — every morning, before the open.' },
  { to: '/app/screener', title: '② Stock Screener', desc: `Fundamentals filters over the ${universeCountLabel(universeCount)} MSA universe across six exchanges — NGX, NSE-Kenya, JSE, GSE, EGX, BRVM.` },
  { to: '/app/backtest', title: '③ Portfolio & Backtest', desc: 'Construct a portfolio, run institutional risk analytics, and backtest on split/bonus/dividend-adjusted prices.' },
  { to: '/app/research', title: '④ Research Marketplace', desc: 'Publish and discover research ideas, with a verified track-record ledger. Strategy copier is Phase 2.' },
]; }

const EXCHANGES = ['NGX', 'NSE', 'JSE', 'GSE', 'EGX', 'BRVM'];

export default function Overview() {
  const [indices, setIndices] = useState(null);
  const [loading, setLoading] = useState(true);
  const universe = useUniverseSummary();
  const modules = workspaceModules(universe?.total);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    msaApi
      .indices()
      .then((data) => alive && setIndices(data))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <Card hero>
        <div className="aq-hero">
          <h1 className="heading">AlphaQap</h1>
          <p className="aq-hero-sub">
            Your African-markets investment workspace. Brief, screen, construct, backtest, and
            publish — with real MyStocks Africa data and a proven research engine behind it.
            Qaptal × AlphaQuark. Technology layer only: no advice, no custody, no execution.
          </p>
        </div>
      </Card>

      <section>
        <h2 className="aq-section-title heading">What the workspace does</h2>
        <p className="aq-module-desc aq-landing-lead">
          Everything you need to go from market signal to a decision-ready portfolio: a daily
          brief to start the day, a six-exchange screener to find candidates, an institutional
          backtesting engine to validate your edge, and a research marketplace to publish what
          you find — with a verified track record behind it.
        </p>
        <div className="aq-grid aq-grid--4">
          {modules.map((m) => (
            <Link key={m.to} to={m.to} className="aq-module-link">
              <Card>
                <h3 className="heading" style={{ fontSize: 16, marginBottom: 8 }}>
                  {m.title}
                </h3>
                <p className="aq-module-desc">{m.desc}</p>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="aq-section-title heading">Markets today</h2>
        <div className="aq-grid aq-grid--4">
          {loading && <Card>Loading indices…</Card>}
          {!loading &&
            (indices?.items || []).map((idx) => (
              <StatCard
                key={idx.symbol}
                label={idx.name}
                value={number(idx.value)}
                delta={idx.changePct}
                meta={indexMeta(idx)}
              />
            ))}
          {!loading && (!indices || indices.items.length === 0) && (
            <Card>
              <div className="aq-empty-state">No index data available.</div>
            </Card>
          )}
        </div>
      </section>

      <section>
        <h2 className="aq-section-title heading">Covered universe</h2>
        <Card>
          <div className="aq-chip-row">
            {EXCHANGES.map((ex) => (
              <Chip key={ex} tone="info">
                {ex}
              </Chip>
            ))}
          </div>
          <p className="aq-module-desc" style={{ marginTop: 12 }}>
            Six exchanges under Schedule A (NGX, NSE-Kenya, JSE, GSE, EGX, BRVM), served via
            MyStocks Africa's Market Data lane.
          </p>
        </Card>
      </section>

      <section>
        <h2 className="aq-section-title heading">Plans</h2>
        <div className="aq-grid aq-grid--3">
          {TIER_PLANS.map((plan) => (
            <Card key={plan.id} className={planCardClassName(plan.popular)}>
              {plan.popular && <span className="aq-plan-badge">Most popular</span>}
              <h3 className="heading" style={{ fontSize: 18 }}>
                {plan.name}
              </h3>
              <p className="aq-plan-price num">{plan.price}</p>
              <p className="aq-module-desc">{plan.blurb}</p>
              {plan.popular && <Chip tone="info">Recommended</Chip>}
              <Link to="/app/billing" className="aq-btn aq-btn--primary aq-btn--sm aq-plan-cta">
                {plan.id === 'payg' ? 'Top up credits' : 'Subscribe'}
              </Link>
            </Card>
          ))}
        </div>
      </section>
    </>
  );
}
